import { toastError, toastSuccess, toastWarning } from 'admination-react'
import { useEffect, useRef, useState } from 'react'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { useApp } from '@/core/state/appStore'
import { useVerbisLebenslaufStatus } from './useVerbisLebenslaufStatus'
import type { VerbisLebenslaufEintrag } from './VerbisLebenslaufHochladen'
import { fehlerZeileText } from './prompts'
import { classifyLebenslaufZeitraumFailure, classifyVerbisLebenslaufUploadFailure } from './errors'
import { hinzufuegenFehler, KEINE_DATEI_TEXT } from './dateien'
import { hinweiseToastText, type ZeitraumSpeichern } from './hinweise'
import { boxOffen, istBoxOffen, setUpload, useLaufenderUpload, type LaufenderUpload } from './laufendeUploads'
import {
  lebenslaufZeilenOffen,
  setLebenslaufZeitraum,
  uploadVerbisLebenslauf,
  type VerbisLebenslaufErrorBody,
  type VerbisLebenslaufHinweis,
  type VerbisLebenslaufUploadResult,
  type VerbisLebenslaufZuordnung,
} from '../../api/verbisLebenslauf'

function zaehle(n: number, einzahl: string, mehrzahl: string): string {
  return `${n} ${n === 1 ? einzahl : mehrzahl}`
}

export function erfolgText({ verbis, lebenslauf }: VerbisLebenslaufUploadResult): string {
  const teile: string[] = []
  if (verbis) {
    const felder = zaehle(verbis.von, 'Feld', 'Feldern')
    const dateien = zaehle(verbis.dateien, 'VerBIS-Screenshot', 'VerBIS-Screenshots')
    teile.push(`${verbis.gelesen} von ${felder} aus ${dateien} ausgelesen`)
  }
  if (lebenslauf) {
    const eintraege = zaehle(lebenslauf.eintraege, 'Eintrag', 'Einträge')
    const seiten = zaehle(lebenslauf.seiten, 'Lebenslauf-Seite', 'Lebenslauf-Seiten')
    teile.push(`${eintraege} aus ${seiten} ausgelesen`)
  }
  return teile.join(' · ')
}

export const ZEITRAUM_GESPEICHERT_TEXT = 'Datum gespeichert.'

function mitZuordnung(
  eintraege: VerbisLebenslaufEintrag[],
  zuordnung: readonly VerbisLebenslaufZuordnung[],
): VerbisLebenslaufEintrag[] {
  if (zuordnung.length !== eintraege.length) return eintraege
  return eintraege.map((eintrag, index) => ({ ...eintrag, art: zuordnung[index].art }))
}

export function useVerbisLebenslaufUpload(coachingId: string | null) {
  const { dispatch, reloadDashboard, coachings } = useApp()
  const {
    done,
    loading: statusLoading,
    statusError,
    markDone,
    reload: reloadStatus,
    ohneDatum,
    korrigiert,
  } = useVerbisLebenslaufStatus(coachingId)
  const [eintraege, setEintraege] = useState<VerbisLebenslaufEintrag[]>([])
  const nextIdRef = useRef(0)
  const [fehlerPrompt, setFehlerPrompt] = useState<VerbisLebenslaufErrorBody | null>(null)
  const [hinweise, setHinweise] = useState<readonly VerbisLebenslaufHinweis[]>([])
  const laufend = useLaufenderUpload(coachingId)
  const uploading = laufend?.phase === 'laeuft'

  useEffect(() => (coachingId ? boxOffen(coachingId) : undefined), [coachingId])

  useEffect(() => setHinweise([]), [coachingId])
  const coachingIdRef = useRef(coachingId)
  coachingIdRef.current = coachingId

  const uebernommenRef = useRef<LaufenderUpload | undefined>(undefined)
  useEffect(() => {
    if (!laufend || laufend === uebernommenRef.current) return
    uebernommenRef.current = laufend
    if (laufend.phase === 'fehler') {
      setEintraege([...laufend.eintraege])
      nextIdRef.current = Math.max(nextIdRef.current, ...laufend.eintraege.map((eintrag) => eintrag.id + 1))
      if (laufend.failure.kind === 'prompt') setFehlerPrompt(laufend.failure.body)
      if (laufend.failure.reload) reloadStatus()
    } else if (laufend.phase === 'fertig') {
      if (laufend.result.verbis) markDone('verbis')
      if (laufend.result.lebenslauf) {
        markDone('lebenslauf')
        setHinweise(laufend.result.lebenslauf.hinweise)
        reloadStatus()
      }
    }
  }, [laufend, markDone, reloadStatus])

  function fuerPerson(id: string, text: string): string {
    const name = coachings.find((coaching) => coaching.id === id)?.name
    return name ? `${name}: ${text}` : text
  }

  const saveZeitraum: ZeitraumSpeichern = async (position, zeitraum) => {
    if (!coachingId) throw new Error('Lebenslauf-Datum ohne Person gespeichert')
    const identity = captureWriteIdentity()
    try {
      const ergebnis = await setLebenslaufZeitraum(coachingId, position, zeitraum)
      if (!isWriteIdentityCurrent(identity)) return null
      if (coachingIdRef.current === coachingId) {
        korrigiert(position, ergebnis.eintrag, ergebnis.ohne_datum)
        if (!lebenslaufZeilenOffen(ergebnis.ohne_datum)) {
          setHinweise((prev) => prev.filter((hinweis) => hinweis.feld !== 'zeitraum'))
        }
      }
      if (ergebnis.weiter) {
        dispatch({ type: 'CLOSE_FOR', coachingId })
        reloadDashboard()
      }
      toastSuccess(fuerPerson(coachingId, ZEITRAUM_GESPEICHERT_TEXT))
      return null
    } catch (e) {
      const failure = classifyLebenslaufZeitraumFailure(e)
      if (!isWriteIdentityCurrent(identity)) return failure
      console.error('Lebenslauf-Datum speichern fehlgeschlagen:', e)
      toastError(fuerPerson(coachingId, failure.message))
      if (failure.reload) {
        reloadDashboard()
        if (coachingIdRef.current === coachingId) reloadStatus()
      }
      return failure
    }
  }

  function add(files: File[]) {
    if (uploading) return
    const fehler = hinzufuegenFehler(
      eintraege.map((eintrag) => eintrag.file),
      files,
    )
    if (fehler) {
      toastError(fehler)
      return
    }
    const neu = files.map((file) => ({ id: nextIdRef.current++, file, art: null }))
    setEintraege((prev) => [...prev, ...neu])
  }

  function remove(id: number) {
    if (uploading) return
    setEintraege((prev) => prev.filter((eintrag) => eintrag.id !== id))
  }

  async function upload() {
    if (!coachingId || uploading) return
    const identity = captureWriteIdentity()
    if (eintraege.length === 0) {
      toastError(KEINE_DATEI_TEXT)
      return
    }
    const gesendet = eintraege
    const fuer = (text: string) => fuerPerson(coachingId, text)
    setEintraege([])
    setFehlerPrompt(null)
    setUpload(coachingId, { phase: 'laeuft', eintraege: gesendet }, identity)
    try {
      const result = await uploadVerbisLebenslauf(
        coachingId,
        gesendet.map((eintrag) => eintrag.file),
      )
      setUpload(coachingId, { phase: 'fertig', result }, identity)
      if (!isWriteIdentityCurrent(identity)) return
      toastSuccess(fuer(erfolgText(result)))
      const neueHinweise = result.lebenslauf?.hinweise ?? []
      if (neueHinweise.length > 0) {
        if (!istBoxOffen(coachingId)) toastWarning(fuer(hinweiseToastText(neueHinweise)))
      } else if (result.weiter) {
        dispatch({ type: 'CLOSE_FOR', coachingId })
      }
      reloadDashboard()
    } catch (e) {
      const failure = classifyVerbisLebenslaufUploadFailure(e)
      setUpload(coachingId, { phase: 'fehler', eintraege: mitZuordnung(gesendet, failure.zuordnung), failure }, identity)
      if (!isWriteIdentityCurrent(identity)) return
      console.error('VerBIS/Lebenslauf-Upload fehlgeschlagen:', e)
      if (failure.kind === 'toast') {
        toastError(fuer(failure.message))
      } else if (!istBoxOffen(coachingId)) {
        toastError(fuer([failure.body.meldung, ...failure.body.fehler.map(fehlerZeileText)].join(' ')))
      }
      if (failure.reload) reloadDashboard()
    }
  }

  return {
    done,
    statusLoading,
    statusError,
    reloadStatus,
    ohneDatum,
    uploading,
    eintraege: laufend?.phase === 'laeuft' ? laufend.eintraege : eintraege,
    hinweise,
    fehlerPrompt,
    closeFehlerPrompt: () => setFehlerPrompt(null),
    fuerPerson,
    saveZeitraum,
    add,
    remove,
    upload,
  }
}
